import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import PlaceIcon from '@mui/icons-material/Place';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { memo, useState } from 'react';
import type { Meeting } from '@penticton-dev-events/shared';

export type UpcomingMeetingsProps = {
  readonly meetings: Meeting[];
  readonly loading?: boolean;
  readonly onMeetingClick?: (meeting: Meeting) => void;
  readonly showGroupName?: boolean;
  readonly maxMeetings?: number;
};

type MeetingCardImageProps = {
  readonly meeting: Meeting;
};

const MeetingCardImage = ({ meeting }: MeetingCardImageProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  if (!meeting.posterUrl || error) {
    // Fallback gradient when no poster or error loading
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%', // 16:9 aspect ratio
          background: theme =>
            `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.8)} 0%, ${alpha(theme.palette.secondary.main, 0.6)} 100%)`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              textAlign: 'center',
              fontWeight: 600,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            {meeting.title}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%', // 16:9 aspect ratio
        overflow: 'hidden',
        backgroundColor: 'grey.200',
      }}
    >
      {loading && (
        <Skeleton
          variant="rectangular"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />
      )}
      <Box
        component="img"
        src={meeting.posterUrl}
        alt={meeting.posterAlt || `${meeting.title} poster`}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />
      {/* Gradient overlay for better text readability */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
        }}
      />
      {/* Title overlay on image */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            fontWeight: 600,
            textShadow: '0 2px 8px rgba(0,0,0,0.5)',
          }}
        >
          {meeting.title}
        </Typography>
      </Box>
    </Box>
  );
};

const UpcomingMeetings = memo(
  ({
    meetings,
    loading = false,
    onMeetingClick,
    showGroupName = true,
    maxMeetings = 10,
  }: UpcomingMeetingsProps) => {
    const displayMeetings = meetings.slice(0, maxMeetings);

    if (loading) {
      return (
        <Stack direction="row" flexWrap="wrap" gap={3} justifyContent="flex-start">
          {Array.from({ length: 3 }, (_, index) => (
            <Card
              key={index}
              sx={{
                flexBasis: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33% - 16px)' },
              }}
            >
              <Skeleton variant="rectangular" height={200} />
              <CardContent>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="40%" height={24} />
                <Skeleton variant="text" width="80%" height={20} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      );
    }

    if (displayMeetings.length === 0) {
      return (
        <Card>
          <CardContent>
            <Typography color="text.secondary" textAlign="center">
              No upcoming meetings scheduled.
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <>
        <Stack direction="row" flexWrap="wrap" gap={3} justifyContent="flex-start">
          {displayMeetings.map(meeting => {
            const startDate = new Date(meeting.startsAt);
            const endDate = new Date(meeting.endsAt);
            const isToday = startDate.toDateString() === new Date().toDateString();
            const isTomorrow =
              startDate.toDateString() ===
              new Date(Date.now() + 24 * 60 * 60 * 1000).toDateString();

            return (
              <Card
                key={meeting.id}
                sx={{
                  flexBasis: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33% - 16px)' },
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme => theme.shadows[8],
                  },
                }}
              >
                <CardActionArea
                  onClick={onMeetingClick ? () => onMeetingClick(meeting) : undefined}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    height: '100%',
                  }}
                >
                  <MeetingCardImage meeting={meeting} />

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack spacing={1.5}>
                      {/* Status Chips */}
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          label={meeting.status}
                          color={meeting.status === 'scheduled' ? 'success' : 'default'}
                          size="small"
                        />
                        {isToday && <Chip label="Today" color="primary" size="small" />}
                        {isTomorrow && (
                          <Chip label="Tomorrow" color="secondary" size="small" />
                        )}
                        {/* Event Type Chip */}
                        <Chip
                          size="small"
                          icon={
                            meeting.eventType === 'in-person' ? (
                              <PlaceIcon />
                            ) : meeting.eventType === 'online' ? (
                              <VideocamIcon />
                            ) : undefined
                          }
                          label={
                            meeting.eventType === 'in-person'
                              ? 'In-Person'
                              : meeting.eventType === 'online'
                                ? 'Online'
                                : 'Hybrid'
                          }
                          color={
                            meeting.eventType === 'in-person'
                              ? 'info'
                              : meeting.eventType === 'online'
                                ? 'secondary'
                                : 'warning'
                          }
                          variant="outlined"
                        />
                      </Stack>

                      {/* Date and Time */}
                      <Typography variant="body2" color="text.secondary">
                        <CalendarTodayIcon
                          sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }}
                        />
                        {startDate.toLocaleDateString(undefined, {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}{' '}
                        {startDate.toLocaleTimeString(undefined, {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                        {' - '}
                        {endDate.toLocaleTimeString(undefined, {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </Typography>

                      {/* Location/Online info */}
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(meeting.eventType === 'in-person' ||
                          meeting.eventType === 'hybrid') &&
                          meeting.location && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                            >
                              <PlaceIcon sx={{ fontSize: 14 }} />
                              {meeting.location.label}
                            </Typography>
                          )}
                        {(meeting.eventType === 'online' ||
                          meeting.eventType === 'hybrid') &&
                          meeting.onlineUrl && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                            >
                              <VideocamIcon sx={{ fontSize: 14 }} />
                              Online Available
                            </Typography>
                          )}
                      </Stack>

                      {/* Description */}
                      {meeting.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minHeight: '2.5em',
                          }}
                        >
                          {meeting.description}
                        </Typography>
                      )}

                      {/* Attendance Info */}
                      <Typography variant="caption" color="text.secondary">
                        {meeting.attendeeCount} attending
                        {meeting.capacity &&
                          ` • ${meeting.capacity - meeting.attendeeCount} spots remaining`}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>

        {meetings.length > maxMeetings && (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
            And {meetings.length - maxMeetings} more meetings...
          </Typography>
        )}
      </>
    );
  },
);

UpcomingMeetings.displayName = 'UpcomingMeetings';

export default UpcomingMeetings;
